import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Heartbeat config (Feb 2026 — fix "session se ferme rapidement")
// Pings /auth/me at fixed interval to keep the cookie session alive while the
// browser tab stays open, even if the user is inactive (e.g. another tab focused).
const HEARTBEAT_INTERVAL_MS = 15 * 60 * 1000;  // 15 min
const FOCUS_REFRESH_THRESHOLD_MS = 30 * 60 * 1000;  // 30 min inactive → refresh on focus

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    // Si l'utilisateur arrive directement sur /login (ou variantes publiques),
    // inutile de tester l'auth — il n'a aucune session. Évite 1 requête 401
    // bruyante au tout premier chargement de l'app.
    const path = typeof window !== 'undefined' ? window.location?.pathname || '' : '';
    const PUBLIC_PATHS = ['/login', '/2fa', '/reset-password'];
    if (PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + '/'))) {
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
      lastActivityRef.current = Date.now();
    } catch (error) {
      // 401 attendu si pas de session valide → on bascule en non-authentifié.
      // On NE rappelle PAS /auth/logout : il n'y a aucune session à invalider
      // (et ça génèrerait un second 401 polluant la console).
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  // ── Heartbeat pour maintenir la session vivante (Feb 2026) ────────────────
  // Si l'utilisateur reste connecté mais inactif (autre onglet, pause café…),
  // on ping /auth/me toutes les 15 min pour garder le cookie session chaud
  // et déclencher un refresh automatique de l'access_token avant expiration.
  useEffect(() => {
    if (!user || user === false) return;  // pas connecté → pas de heartbeat

    const ping = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        lastActivityRef.current = Date.now();
      } catch (e) {
        // Si 401, l'interceptor api.js gère la redirection vers /login
      }
    };

    heartbeatRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(heartbeatRef.current);
  }, [user]);

  // ── Refresh au retour de focus si inactif > 30 min ────────────────────────
  useEffect(() => {
    const onFocus = () => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs > FOCUS_REFRESH_THRESHOLD_MS && user && user !== false) {
        // Refresh session si > 30 min d'inactivité
        axios.get(`${API}/auth/me`, { withCredentials: true })
          .then((r) => { setUser(r.data); lastActivityRef.current = Date.now(); })
          .catch(() => {});
      } else {
        lastActivityRef.current = Date.now();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onFocus();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [user]);

  const login = async (telephone, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { telephone, password },
        { withCredentials: true }
      );
      // Cas 2FA : pas de cookies poses, on retourne le ticket sans setUser
      if (response.data?.requires_2fa) {
        return {
          success: true,
          requires_2fa: true,
          ticket: response.data.ticket,
          expires_in: response.data.expires_in,
        };
      }
      setUser(response.data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Cas 1 : detail est une chaine -> on l'affiche
      if (typeof detail === 'string') {
        return { success: false, error: detail };
      }
      // Cas 2 : detail est un tableau (erreur de validation Pydantic 422)
      if (Array.isArray(detail) && detail[0]?.msg) {
        return { success: false, error: `Champ invalide : ${detail[0].msg}` };
      }
      // Cas 3 : detail est un objet avec un message (ex: PENDING_APPROVAL, REJECTED)
      if (detail && typeof detail === 'object' && detail.message) {
        return { success: false, error: detail.message, error_code: detail.code, error_detail: detail };
      }
      // Cas 4 : pas de reponse du serveur (reseau / CORS / serveur down)
      if (!error.response) {
        return { success: false, error: 'Pas de reponse du serveur. Verifiez votre connexion internet.' };
      }
      // Cas 5 : statut HTTP avec corps non-JSON
      const status = error.response.status;
      if (status === 401) return { success: false, error: 'Telephone ou mot de passe incorrect' };
      if (status === 404) return { success: false, error: 'Service de connexion introuvable (404)' };
      if (status >= 500) return { success: false, error: `Erreur serveur (${status}). Reessayez dans quelques secondes.` };
      return { success: false, error: `Erreur ${status}` };
    }
  };

  const verify2FA = async (ticket, code) => {
    try {
      const response = await axios.post(
        `${API}/auth/2fa/verify`,
        { ticket, code },
        { withCredentials: true }
      );
      // _issue_session_cookies retourne le payload user complet
      setUser(response.data);
      return { success: true, used_recovery_code: response.data?.used_recovery_code };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: typeof detail === 'string' ? detail : 'Code 2FA invalide',
      };
    }
  };

  const register = async (nom, telephone, password, poste, role = 'agent') => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { nom, telephone, password, poste, role },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return { 
        success: false, 
        error: typeof detail === 'string' ? detail : 'Erreur d\'inscription' 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    verify2FA,
    register,
    logout,
    fetchUser: checkAuth,
    isChef: user && user.role === 'chef_agence'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};